/*
 * Copyright 2022 Nordeck IT + Consulting GmbH
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { extractWidgetParameters } from '@matrix-widget-toolkit/api';
import LanguageDetector, {
  CustomDetector,
} from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

/**
 * Backend for usage with `i18n-next` that provides translations for internal
 * messages.
 */
export const WidgetToolkitI18nBackend = resourcesToBackend((lng, ns, clb) => {
  import(`../locales/${lng}/${ns}.json`)
    .then((resources) => clb(null, resources))
    .catch((err) => clb(err, undefined));
});

/**
 * A language detector that reads the language from the widget url.
 */
export class WidgetApiLanguageDetector extends LanguageDetector {
  constructor() {
    super(undefined, {
      // prefer the widget api and fallback to the browser settings.
      order: ['widgetApi', 'navigator'],
    });

    const widgetApiLanguageDetector: CustomDetector = {
      name: 'widgetApi',
      lookup: () => {
        const { clientLanguage } = extractWidgetParameters();
        return clientLanguage;
      },
    };

    this.addDetector(widgetApiLanguageDetector);
  }
}
